import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Effect } from "../types/effects";
import { LibraryItem } from "../types/library";
import dreamWorldProvider from "../DreamWorldProvider";
import PageLayout from "../components/ui/PageLayout";
import { ArrowLeft, Sparkles, Share2 } from "lucide-react";
import ContentModal from "../components/ContentModal";
import LoginPromptModal from "../components/modals/LoginPromptModal";
import CreationReadyModal from "../components/CreationReadyModal";
import EffectDescriptionPanel from "../components/create/EffectDescriptionPanel";
import { Style } from "../types/effects";
import uploadImageToFirebase from "../utils/uploadImageToFirebase";
import { Modality } from "@google/genai";
import { Timestamp } from "firebase/firestore";
import { v4 as uuidv4 } from "uuid";
import { useSelector } from "react-redux";
import { RootState } from "../store/store";
import { InteractiveEditingData } from "../types/library";

const EffectDisplayPage: React.FC = () => {
  const userGlobalData = useSelector(
    (state: RootState) => state.dreamworld.userGlobalData
  );
  const { effectId } = useParams<{ effectId: string }>();
  const navigate = useNavigate();
  const [effect, setEffect] = useState<Effect | null>(null);
  const [content, setContent] = useState<LibraryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [isContentModalOpen, setIsContentModalOpen] = useState(false);
  const [selectedContentId, setSelectedContentId] = useState<string | null>(
    null
  );
  const [isEffectDescriptionPanelOpen, setIsEffectDescriptionPanelOpen] =
    useState(false);
  const [isLoginPromptModalOpen, setIsLoginPromptModalOpen] = useState(false);
  const [isCreationReadyModalOpen, setIsCreationReadyModalOpen] =
    useState(false);
  const [newCreationId, setNewCreationId] = useState<string | null>(null);

  useEffect(() => {
    const fetchEffectAndContent = async () => {
      if (!effectId) return;
      setLoading(true);
      try {
        const effectData = await dreamWorldProvider.getEffect(effectId);
        if (effectData) {
          setEffect(effectData);
        } else {
          console.error("No such effect!");
        }

        // Fetch content with this effect
        const effectContent =
          await dreamWorldProvider.getPublishedContentByEffect(effectId);
        setContent(effectContent);
      } catch (error) {
        console.error("Error fetching effect or content:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchEffectAndContent();
  }, [effectId]);

  const handleUseEffect = () => {
    if (!userGlobalData) {
      setIsLoginPromptModalOpen(true);
    } else if (effect) {
      setIsEffectDescriptionPanelOpen(true);
    }
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: `Check out this effect: ${effect?.title}`,
        text: effect?.description,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert("Link copied to clipboard!");
    }
  };

  const handleContentClick = (contentId: string) => {
    setSelectedContentId(contentId);
    setIsContentModalOpen(true);
  };

  const selectedContent = content.find((item) => item.id === selectedContentId);

  const handleStartEditing = async (
    effect: Effect,
    images: File[],
    userInput?: string,
    style?: Style
  ) => {
    if (!userGlobalData || images.length === 0) return;

    // setIsEditing(true);

    try {
      const originalImageUrls = await Promise.all(
        images.map((image) => uploadImageToFirebase(image, "originalImages"))
      );

      const ai = dreamWorldProvider.getImageAI();

      const imageParts = await Promise.all(
        images.map(async (image) => {
          const fileAsBase64 = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(image);
            reader.onload = () =>
              resolve((reader.result as string).split(",")[1]);
            reader.onerror = (error) => reject(error);
          });
          return {
            inlineData: {
              mimeType: image.type,
              data: fileAsBase64,
            },
          };
        })
      );

      let promptText = userInput
        ? `${effect.prompt} User input: ${userInput}`
        : effect.prompt;

      if (style) {
        promptText = `${promptText} Style: ${style.name}`;
      }

      const contents = [{ text: promptText }, ...imageParts];

      const response = await ai.models.generateContent({
        model: "gemini-2.5-flash-image-preview",
        contents: contents,
        config: {
          responseModalities: [Modality.TEXT, Modality.IMAGE],
        },
      });

      if (
        response.candidates &&
        response.candidates.length > 0 &&
        response.candidates[0].content &&
        response.candidates[0].content.parts
      ) {
        for (const part of response.candidates[0].content.parts) {
          if (part.inlineData && part.inlineData.data) {
            const imageData = part.inlineData.data;
            const byteCharacters = atob(imageData);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
              byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: "image/png" });
            const file = new File([blob], "edited_image.png", {
              type: "image/png",
            });
            const generatedImageUrl = await uploadImageToFirebase(
              file,
              "postsImages"
            );

            const docId = uuidv4();
            var newEditingData: InteractiveEditingData = {
              id: docId,
              ownerId: userGlobalData.user.uid,
              title: effect.title,
              originalImageUrls: originalImageUrls,
              createdAt: Timestamp.now(),
              turnsCount: 1,
              effectId: effect.id,
              editings: [
                {
                  id: uuidv4(),
                  imageUrl: originalImageUrls,
                  request: promptText,
                  generatedImageUrl,
                  date: Timestamp.now(),
                  isSavedToLib: false,
                  isPublished: false,
                },
              ],
            };

            await dreamWorldProvider.addInteractiveEditingData(newEditingData);
            setNewCreationId(docId);
            setIsCreationReadyModalOpen(true);
            setIsEffectDescriptionPanelOpen(false);
          }
        }
      }
    } catch (error) {
      console.error("Error starting interactive editing:", error);
    } finally {
      // setIsEditing(false);
      // setSelectedEffect(null);
    }
  };

  const handleLike = (id: string) => {
    // This is a placeholder. A real implementation would involve a Redux action
    // or a direct API call to update the like status.
    console.log(`Toggling like for content ${id}`);
    const updatedContent = content.map((item) =>
      item.id === id ? { ...item, isLiked: !item.isLiked } : item
    );
    setContent(updatedContent);
  };

  if (loading) {
    return (
      <PageLayout>
        <div className="text-white">Loading...</div>
      </PageLayout>
    );
  }

  if (!effect) {
    return (
      <PageLayout>
        <div className="text-white">Effect not found.</div>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <div className="p-4 md:p-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-white/80 hover:text-white mb-6"
        >
          <ArrowLeft size={20} />
          Back
        </button>
        <div className="flex flex-col md:flex-row gap-8">
          <div className="md:w-1/3">
            {effect.image && (
              <img
                src={effect.image}
                alt={effect.title}
                className="rounded-2xl shadow-lg mb-4 w-full aspect-square object-cover"
              />
            )}
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
              {effect.title}
            </h1>
            <p className="text-white/70 text-lg mb-4">{effect.description}</p>
            {effect.creator && (
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <img
                    src={effect.creator.avatarUrl}
                    alt={effect.creator.username}
                    className="w-10 h-10 rounded-full"
                  />
                  <span className="text-white font-semibold">
                    {effect.creator.username}
                  </span>
                </div>
                <button
                  onClick={() =>
                    effect.creator && navigate(`/profile/${effect.creator.uid}`)
                  }
                  className="px-4 py-2 bg-gray-700 text-white rounded-xl hover:bg-gray-600 transition-all"
                >
                  View Profile
                </button>
              </div>
            )}
            <div className="flex gap-2">
              <button
                onClick={handleUseEffect}
                className="w-full flex items-center justify-center gap-3 p-4 bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded-xl font-semibold text-lg hover:shadow-lg transition-all"
              >
                <Sparkles size={20} />
                Use this Effect
              </button>
              <button
                onClick={handleShare}
                className="p-4 bg-gray-700 text-white rounded-xl hover:bg-gray-600 transition-all"
              >
                <Share2 size={20} />
              </button>
            </div>
          </div>
          <div className="md:w-2/3">
            <h2 className="text-2xl font-bold text-white mb-4">
              Creations using this effect
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {content.map((item) => (
                <div
                  key={item.id}
                  className="aspect-square cursor-pointer"
                  onClick={() => handleContentClick(item.id)}
                >
                  <img
                    src={item.thumbnailUrl || item.fullUrl}
                    alt={item.title}
                    className="w-full h-full object-cover rounded-lg"
                  />
                </div>
              ))}
            </div>
            {content.length === 0 && (
              <div className="text-center py-10 bg-white/5 rounded-lg">
                <p className="text-white/60">
                  No creations have been published with this effect yet.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
      {isContentModalOpen && selectedContent && (
        <ContentModal
          contentId={selectedContent.id}
          initialContent={selectedContent}
          source="library"
          onClose={() => setIsContentModalOpen(false)}
          onLike={handleLike}
        />
      )}
    <LoginPromptModal
      isOpen={isLoginPromptModalOpen}
      onClose={() => setIsLoginPromptModalOpen(false)}
    />
      <EffectDescriptionPanel
        effect={isEffectDescriptionPanelOpen ? effect : null}
        onClose={() => setIsEffectDescriptionPanelOpen(false)}
        onStartEditing={handleStartEditing}
      />
      <CreationReadyModal
        isOpen={isCreationReadyModalOpen}
        onClose={() => setIsCreationReadyModalOpen(false)}
        onNavigate={() => {
          if (newCreationId) {
            navigate(`/interactive-editing/${newCreationId}`);
          }
        }}
      />
    </PageLayout>
  );
};

export default EffectDisplayPage;
